// Fetches tenant status and either blocks access (students/parents/teachers)
// or shows a reminder banner (admins/managers).
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { AlertTriangle, Lock, CreditCard, X } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';

const HIDE_BANNER_KEY = 'trialBannerHiddenAt';

// Admin/manager can still access these routes even when the tenant is blocked.
// Matched as substrings so it works with slugged URLs like /<slug>/school/subscription.
const BLOCKED_ADMIN_ALLOWED_SUFFIXES = [
  '/school/subscription',
  '/school/profile',
  '/school/settings',
];
const BLOCKED_ADMIN_ALLOWED_PREFIXES = [
  '/payment',
  '/admin',
];

export const TenantAccessGate = ({ children }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bannerHidden, setBannerHidden] = useState(false);

  useEffect(() => {
    if (!user?.tenant_id) { setLoading(false); return; }
    api.get(`/tenants/${user.tenant_id}/status`)
      .then(res => setStatus(res.data))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));

    // Hide banner if user clicked X in last 12h
    try {
      const t = parseInt(localStorage.getItem(HIDE_BANNER_KEY) || '0', 10);
      if (Date.now() - t < 12 * 3600 * 1000) setBannerHidden(true);
    } catch (e) { /* ignore */ }
  }, [user?.tenant_id]);

  // Reset banner hidden state when route changes to subscription (so admin sees urgency)
  useEffect(() => {
    if (location.pathname.includes('/school/subscription')) {
      setBannerHidden(false);
    }
  }, [location.pathname]);

  if (loading || !status) return children;

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdminOrManager = user && ['school_admin', 'manager'].includes(user.role);
  const t = (fr, en) => language === 'fr' ? fr : en;

  // Super admin bypasses everything
  if (isSuperAdmin) return children;

  // Blocked: students / teachers / parents -> full block screen
  if (status.access === 'blocked' && !isAdminOrManager) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="tenant-blocked-screen">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {t('École momentanément indisponible', 'School temporarily unavailable')}
          </h1>
          <p className="text-slate-600 mb-6">
            {status.banner === 'deactivated'
              ? t(
                  "Votre école a été désactivée par l'administrateur de la plateforme. Merci de contacter votre administrateur pour plus d'informations.",
                  'Your school has been deactivated by the platform administrator. Please contact your administrator for more information.'
                )
              : t(
                  "Votre école n'a pas encore réglé son abonnement. Merci de contacter votre administrateur ou un manager pour la débloquer.",
                  'Your school subscription has not been renewed yet. Please contact your administrator or a manager to unblock it.'
                )}
          </p>
          <Button variant="outline" onClick={() => { localStorage.clear(); navigate('/login'); }} data-testid="blocked-logout-btn">
            {t('Retour à la connexion', 'Back to login')}
          </Button>
        </div>
      </div>
    );
  }

  // Blocked: admin / manager
  if (status.access === 'blocked' && isAdminOrManager) {
    // Deactivated by the platform admin: full block (paying won't help)
    if (status.banner === 'deactivated') {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="tenant-deactivated-screen">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {t('École désactivée', 'School deactivated')}
            </h1>
            <p className="text-slate-600 mb-6">
              {t(
                "Votre école a été désactivée par l'administrateur de la plateforme. Veuillez le contacter pour la réactiver.",
                'Your school has been deactivated by the platform administrator. Please contact them to reactivate it.'
              )}
            </p>
            <Button variant="outline" onClick={() => { localStorage.clear(); navigate('/login'); }} data-testid="deactivated-logout-btn">
              {t('Retour à la connexion', 'Back to login')}
            </Button>
          </div>
        </div>
      );
    }
    // Payment issue: force to subscription page (except allowed prefixes)
    const path = location.pathname;
    const isAllowed =
      BLOCKED_ADMIN_ALLOWED_PREFIXES.some(p => path.startsWith(p)) ||
      BLOCKED_ADMIN_ALLOWED_SUFFIXES.some(s => path.includes(s));
    if (!isAllowed) {
      const target = user?.tenant_slug
        ? `/${user.tenant_slug}/school/subscription`
        : '/school/subscription';
      return <Navigate to={target} replace />;
    }
  }

  // Banner for admin/manager
  const showBanner = isAdminOrManager && !bannerHidden && status.banner;
  const bannerColor = {
    trial: 'bg-blue-50 border-blue-300 text-blue-800',
    expiring_soon: 'bg-amber-50 border-amber-300 text-amber-800',
    expired: 'bg-red-50 border-red-300 text-red-800',
    no_subscription: 'bg-red-50 border-red-300 text-red-800',
  }[status.banner] || 'bg-slate-100 border-slate-300';

  const bannerMessage = () => {
    if (status.banner === 'trial') {
      return t(
        `Essai gratuit : ${status.days_remaining} jour(s) restant(s). Souscrivez à un plan pour continuer.`,
        `Free trial: ${status.days_remaining} day(s) remaining. Subscribe to a plan to continue.`
      );
    }
    if (status.banner === 'expiring_soon') {
      return t(
        `Votre abonnement expire dans ${status.days_remaining} jour(s). Renouvelez pour éviter la coupure.`,
        `Your subscription expires in ${status.days_remaining} day(s). Renew to avoid disruption.`
      );
    }
    if (status.banner === 'expired' || status.banner === 'no_subscription') {
      return t(
        "Abonnement expiré. Les élèves et parents ne peuvent plus accéder à l'école. Renouvelez pour rétablir l'accès.",
        'Subscription expired. Students and parents can no longer access. Renew to restore access.'
      );
    }
    return null;
  };

  return (
    <>
      {showBanner && (
        <div
          className={`${bannerColor} border-t px-4 py-2 flex items-center justify-between gap-3 fixed bottom-0 left-0 right-0 z-50 shadow-lg`}
          data-testid="tenant-banner"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{bannerMessage()}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={() => navigate(user?.tenant_slug ? `/${user.tenant_slug}/school/subscription` : '/school/subscription')}
              data-testid="banner-pay-btn"
            >
              <CreditCard className="w-4 h-4 mr-1" />
              {t('Payer', 'Pay')}
            </Button>
            <button
              type="button"
              onClick={() => {
                setBannerHidden(true);
                try { localStorage.setItem(HIDE_BANNER_KEY, String(Date.now())); } catch (e) { /* ignore */ }
              }}
              className="p-1 hover:bg-black/10 rounded"
              aria-label="Close"
              data-testid="banner-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {children}
    </>
  );
};

export default TenantAccessGate;
